import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaToggleOn, FaToggleOff, FaClipboardList } from 'react-icons/fa';
import QuestionSectionModal from './QuestionSectionModal';
import QuestionEditModal from './QuestionEditModal';
import QuestionPreview from './QuestionPreview';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';

const QuestionManagement = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('1040');
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [editMode, setEditMode] = useState('create');

  const formTypes = ['1040', '1065', '1120-S', '1120'];

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch questions');

      const data = await response.json();
      setQuestions(data.data);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSection = () => {
    setSelectedSection(null);
    setEditMode('create');
    setShowSectionModal(true);
  };

  const handleEditSection = (section) => {
    setSelectedSection(section);
    setEditMode('edit');
    setShowSectionModal(true);
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section/${sectionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete section');

      await fetchQuestions();
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const handleToggleSection = async (sectionId, currentStatus) => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section/${sectionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !currentStatus })
      });

      if (!response.ok) throw new Error('Failed to toggle section');

      await fetchQuestions();
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const handlePreview = (section) => {
    setSelectedSection(section);
    setShowPreview(true);
  };

  const handleSectionSave = async (sectionData) => {
    const token = localStorage.getItem('adminToken');
    try {
      const url = editMode === 'create'
        ? `${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section`
        : `${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/section/${selectedSection._id}`;

      const method = editMode === 'create' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(sectionData)
      });

      if (!response.ok) throw new Error(`Failed to ${editMode} section`);

      await fetchQuestions();
      setShowSectionModal(false);
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const handleInitializeQuestions = async () => {
    if (!window.confirm('This will initialize default questions. Continue?')) return;

    const token = localStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/questions/admin/initialize`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to initialize questions');

      await fetchQuestions();
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const getFormQuestions = (formType) => {
    return questions.filter(q => q.formType === formType);
  };

  const renderQuestionSection = (section) => (
    <div key={section._id} className="bg-white dark:bg-dark-800 rounded-xl shadow-card mb-4 overflow-hidden">
      <div className="flex flex-col md:flex-row md:items-center justify-between p-4 border-b border-gray-100 dark:border-dark-700">
        <div className="mb-3 md:mb-0">
          <h5 className="font-semibold text-gray-900 dark:text-white">{section.sectionTitle}</h5>
          <p className="text-sm text-gray-500 dark:text-gray-400">{section.questions.length} questions • Order: {section.sectionOrder}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => handlePreview(section)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-cyan-600 border border-cyan-200 rounded-lg hover:bg-cyan-50 dark:border-cyan-800 dark:hover:bg-cyan-900/20"><FaEye /> Preview</button>
          <button onClick={() => handleEditSection(section)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary-600 border border-primary-200 rounded-lg hover:bg-primary-50 dark:border-primary-800 dark:hover:bg-primary-900/20"><FaEdit /> Edit</button>
          <button onClick={() => handleToggleSection(section._id, section.isActive)} className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border ${section.isActive ? 'text-green-600 border-green-200 hover:bg-green-50 dark:border-green-800' : 'text-gray-500 border-gray-200 hover:bg-gray-50 dark:border-gray-700'}`}>{section.isActive ? <FaToggleOn /> : <FaToggleOff />} {section.isActive ? 'Active' : 'Inactive'}</button>
          <button onClick={() => handleDeleteSection(section._id)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"><FaTrash /> Delete</button>
        </div>
      </div>
      <div className="p-4">
        <div className="space-y-2">
          {section.questions.slice(0, 3).map((question, index) => (
            <div key={index} className="text-sm"><span className="text-gray-400">Q{index + 1}:</span> <span className="text-gray-700 dark:text-gray-300">{question.text}</span></div>
          ))}
          {section.questions.length > 3 && (<p className="text-sm text-gray-400 mt-2">... and {section.questions.length - 3} more questions</p>)}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <SkeletonLoader variant="grid" items={6} />;
  }

  return (
    <PageLayout>
      <PageHeader
        title="Question Management"
        description={`${questions.length} total sections`}
        icon={FaClipboardList}
        actions={
          <>
            <button
              onClick={handleCreateSection}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
            >
              <FaPlus /> Add New Section
            </button>
            <button
              onClick={handleInitializeQuestions}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
            >
              Initialize Default
            </button>
          </>
        }
      />

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">&times;</button>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card overflow-hidden">
        <div className="flex border-b border-gray-100 dark:border-dark-700 overflow-x-auto">
          {formTypes.map(formType => (
            <button key={formType} onClick={() => setActiveTab(formType)} className={`px-6 py-4 text-sm font-medium whitespace-nowrap transition-all ${activeTab === formType ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50/50 dark:bg-primary-900/20' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}>Form {formType}</button>
          ))}
        </div>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Form {activeTab} Questions</h4>
            <button onClick={handleCreateSection} className="flex items-center gap-2 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"><FaPlus /> Add Section</button>
          </div>
          {getFormQuestions(activeTab).length === 0 ? (
            <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-blue-700 dark:text-blue-400 text-center">No questions found for Form {activeTab}. Click "Add Section" to create your first section.</div>
          ) : (
            <div>{getFormQuestions(activeTab).sort((a, b) => a.sectionOrder - b.sectionOrder).map(renderQuestionSection)}</div>
          )}
        </div>
      </div>

      <QuestionSectionModal show={showSectionModal} onHide={() => setShowSectionModal(false)} onSave={handleSectionSave} section={selectedSection} mode={editMode} activeFormType={activeTab} />
      <QuestionEditModal show={showEditModal} onHide={() => setShowEditModal(false)} section={selectedSection} onSave={fetchQuestions} />
      <QuestionPreview show={showPreview} onHide={() => setShowPreview(false)} section={selectedSection} />
    </PageLayout>
  );
};

export default QuestionManagement;